import { useEffect, useState, useCallback } from "react";
import { Loader2, Megaphone, CheckCircle2, Clock, DollarSign, Save, Mail } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { OVERLINE, H1, KpiCard, Panel, StatusBadge, btnPrimary, money, useAdmin } from "@/components/admin/ui";

const AD_STATUSES = ["pending_setup", "pending_review", "active", "needs_changes", "paused", "cancelled", "expired", "rejected"];
const TABS = [{ k: "campaigns", label: "Campaigns" }, { k: "products", label: "Ad Products" }, { k: "inquiries", label: "Brand Inquiries" }];

function CampaignRow({ ad, canEdit, onChange }) {
  const [busy, setBusy] = useState(false);
  const change = async (status) => {
    setBusy(true);
    try { await api.adminSetAdStatus(ad.id, { status }); toast.success(`Set to ${status.replace(/_/g, " ")}`); onChange(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  const amount = ad.amount_cents || ad.amount || 0;
  return (
    <tr className="border-b border-[#E5E2DC] last:border-0" data-testid={`ad-row-${ad.id}`}>
      <td className="px-4 py-3">
        <div className="text-sm text-[#1F2A22] font-medium">{ad.business_name || ad.setup?.business_name || "—"}</div>
        <div className="text-xs text-[#5C6B61]">{ad.product_name}{ad.state ? ` · ${ad.state}` : ""}</div>
      </td>
      <td className="px-4 py-3 text-sm text-[#5C6B61] capitalize">{(ad.placement_type || "—").replace(/_/g, " ")}</td>
      <td className="px-4 py-3 text-sm text-[#5C6B61] capitalize">{(ad.billing_cycle || "—").replace(/_/g, " ")}</td>
      <td className="px-4 py-3 text-sm text-[#1F2A22] tabular-nums">{money(amount)}</td>
      <td className="px-4 py-3"><StatusBadge status={ad.status} /></td>
      <td className="px-4 py-3 text-right">
        {canEdit && (
          <select value={ad.status} disabled={busy} onChange={(e) => change(e.target.value)}
            className="border border-[#E5E2DC] bg-white px-2 py-1 text-xs outline-none focus:border-[#1F2A22]" data-testid={`ad-status-${ad.id}`}>
            {AD_STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
          </select>
        )}
      </td>
    </tr>
  );
}

function ProductCard({ p, canEdit, onSaved }) {
  const [f, setF] = useState({ monthly_cents: p.monthly_cents, annual_cents: p.annual_cents, one_time_cents: p.one_time_cents, active: p.active, max_advertisers: p.max_advertisers });
  const [busy, setBusy] = useState(false);
  const dollars = (c) => (c == null ? "" : (c / 100).toString());
  const save = async () => {
    setBusy(true);
    try {
      const body = { active: f.active };
      if (p.billing_type === "recurring") { body.monthly_cents = Math.round(Number(f.monthly_cents || 0)); body.annual_cents = Math.round(Number(f.annual_cents || 0)); }
      else body.one_time_cents = Math.round(Number(f.one_time_cents || 0));
      if (f.max_advertisers !== "" && f.max_advertisers != null) body.max_advertisers = Number(f.max_advertisers);
      await api.adminUpdateAdProduct(p.key, body); toast.success("Saved"); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  const dollarInput = "w-28 border border-[#E5E2DC] bg-white px-2 py-1.5 text-sm outline-none focus:border-[#1F2A22]";
  return (
    <div className={`border border-[#E5E2DC] bg-white p-5 ${!f.active ? "opacity-70" : ""}`} data-testid={`ad-product-${p.key}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="font-serif text-lg text-[#1F2A22]">{p.name}{p.badge && <span className="ml-2 text-[10px] uppercase bg-[#FF6A00] text-white px-1.5 py-0.5">{p.badge}</span>}</p>
          <p className="text-xs text-[#5C6B61] mt-0.5 capitalize">{(p.placement_type || "").replace(/_/g, " ")} · {p.billing_type}</p>
        </div>
        <label className="flex items-center gap-1.5 text-xs text-[#1F2A22]"><input type="checkbox" checked={!!f.active} disabled={!canEdit} onChange={(e) => setF({ ...f, active: e.target.checked })} data-testid={`ad-product-active-${p.key}`} /> Active</label>
      </div>
      <div className="flex flex-wrap gap-4 mt-4 text-xs">
        {p.billing_type === "recurring" ? (
          <>
            <label>Monthly $<input type="number" value={dollars(f.monthly_cents)} disabled={!canEdit} onChange={(e) => setF({ ...f, monthly_cents: Math.round(Number(e.target.value) * 100) })} className={`${dollarInput} ml-1`} data-testid={`ad-monthly-${p.key}`} /></label>
            <label>Annual $<input type="number" value={dollars(f.annual_cents)} disabled={!canEdit} onChange={(e) => setF({ ...f, annual_cents: Math.round(Number(e.target.value) * 100) })} className={`${dollarInput} ml-1`} data-testid={`ad-annual-${p.key}`} /></label>
          </>
        ) : (
          <label>One-time $<input type="number" value={dollars(f.one_time_cents)} disabled={!canEdit} onChange={(e) => setF({ ...f, one_time_cents: Math.round(Number(e.target.value) * 100) })} className={`${dollarInput} ml-1`} data-testid={`ad-onetime-${p.key}`} /></label>
        )}
        {p.requires_state && <label>Max sponsors<input type="number" value={f.max_advertisers ?? ""} disabled={!canEdit} onChange={(e) => setF({ ...f, max_advertisers: e.target.value })} className={`${dollarInput} ml-1 w-16`} /></label>}
      </div>
      {canEdit && <button onClick={save} disabled={busy} className={`${btnPrimary} mt-4 py-1.5`} data-testid={`ad-product-save-${p.key}`}>{busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save</button>}
    </div>
  );
}

export default function AdminAdvertising() {
  const { admin } = useAdmin();
  const canEdit = admin?.permissions?.includes("advertising");
  const [tab, setTab] = useState("campaigns");
  const [data, setData] = useState(null);
  const [products, setProducts] = useState(null);
  const load = useCallback(() => {
    api.adminAdvertising().then(setData).catch(() => setData({ advertisements: [], inquiries: [], stats: {} }));
    api.adminAdProducts().then((d) => setProducts(d.products)).catch(() => setProducts([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="py-20 grid place-items-center"><Loader2 className="animate-spin text-[#1F2A22]" /></div>;
  const s = data.stats || {};

  return (
    <div className="p-6 md:p-8" data-testid="admin-advertising">
      <div><span className={OVERLINE}>Commerce</span><h1 className={`${H1} mt-1`}>Advertising</h1></div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
        <KpiCard label="Campaigns" value={s.total ?? 0} icon={Megaphone} testid="ad-kpi-total" />
        <KpiCard label="Active" value={s.active ?? 0} icon={CheckCircle2} testid="ad-kpi-active" />
        <KpiCard label="Pending" value={s.pending ?? 0} icon={Clock} accent testid="ad-kpi-pending" />
        <KpiCard label="Ad MRR" value={money(s.mrr_cents ?? 0)} icon={DollarSign} testid="ad-kpi-mrr" />
      </div>

      <div className="flex gap-1 mt-8 border-b border-[#E5E2DC]">
        {TABS.map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)} className={`px-4 py-2 text-sm font-serif ${tab === t.k ? "text-[#1F2A22] border-b-2 border-[#FF6A00]" : "text-[#5C6B61]"}`} data-testid={`ad-tab-${t.k}`}>
            {t.label}{t.k === "inquiries" && data.inquiries?.length ? ` (${data.inquiries.length})` : ""}
          </button>
        ))}
      </div>

      <div className="mt-6">
        {tab === "campaigns" && (
          <Panel className="overflow-x-auto">
            {data.advertisements.length === 0 ? <p className="p-6 text-sm text-[#5C6B61]" data-testid="ad-empty">No advertising campaigns yet. Placements you sell will appear here.</p> : (
              <table className="w-full min-w-[720px]">
                <thead><tr className="text-left text-[11px] uppercase tracking-wide text-[#5C6B61] border-b border-[#E5E2DC]">
                  <th className="px-4 py-2 font-serif">Advertiser</th><th className="px-4 py-2 font-serif">Placement</th><th className="px-4 py-2 font-serif">Billing</th><th className="px-4 py-2 font-serif">Amount</th><th className="px-4 py-2 font-serif">Status</th><th className="px-4 py-2 font-serif text-right">Change</th>
                </tr></thead>
                <tbody>{data.advertisements.map((ad) => <CampaignRow key={ad.id} ad={ad} canEdit={canEdit} onChange={load} />)}</tbody>
              </table>
            )}
          </Panel>
        )}

        {tab === "products" && (
          !products ? <div className="py-10 grid place-items-center"><Loader2 className="animate-spin text-[#1F2A22]" /></div> :
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="ad-products-grid">
            {products.map((p) => <ProductCard key={p.key} p={p} canEdit={canEdit} onSaved={load} />)}
          </div>
        )}

        {tab === "inquiries" && (
          <div className="space-y-3" data-testid="ad-inquiries">
            {(!data.inquiries || data.inquiries.length === 0) ? <p className="text-sm text-[#5C6B61]" data-testid="ad-inquiries-empty">No brand partnership inquiries yet.</p> :
              data.inquiries.map((q) => (
                <div key={q.id} className="border border-[#E5E2DC] bg-white p-4" data-testid={`ad-inquiry-${q.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div><p className="font-serif text-[#1F2A22]">{q.company_name}</p><p className="text-xs text-[#5C6B61]">{q.contact_name}</p></div>
                    <StatusBadge status={q.status} />
                  </div>
                  <div className="flex flex-wrap gap-x-5 gap-y-1 mt-2 text-xs text-[#5C6B61]">
                    <a href={`mailto:${q.email}`} className="inline-flex items-center gap-1 hover:text-[#FF6A00]"><Mail size={12} /> {q.email}</a>
                    {q.phone && <span>{q.phone}</span>}
                    {q.website && <a href={q.website} target="_blank" rel="noreferrer" className="hover:text-[#FF6A00]">{q.website}</a>}
                  </div>
                  {q.message && <p className="text-sm text-[#1F2A22] mt-2 whitespace-pre-line">{q.message}</p>}
                </div>
              ))}
          </div>
        )}
      </div>
    </div>
  );
}
